import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PortableText } from "@portabletext/react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import CtaBand from "../components/CtaBand";
import ctaStudio from "../assets/heroes/cta-studio.webp";
import NotFound from "./NotFound";
import { fetchPost, fetchRelated, urlFor } from "../sanity";

const SITE_URL = "https://kinewsstudiolimited.co.uk";

const dateFmt = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

const components = {
  block: {
    normal: ({ children }) => (
      <p className="mt-5 text-pretty text-[17px] leading-[1.8] text-black/75">{children}</p>
    ),
    h2: ({ children }) => (
      <h2 className="mt-12 text-2xl font-semibold leading-tight tracking-tight text-black md:text-3xl">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="mt-8 text-xl font-semibold leading-snug text-black">{children}</h3>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="mt-5 list-disc space-y-2 pl-6 text-[17px] leading-[1.8] text-black/75 marker:text-pink-500">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="mt-5 list-decimal space-y-2 pl-6 text-[17px] leading-[1.8] text-black/75 marker:font-semibold marker:text-pink-600">
        {children}
      </ol>
    ),
  },
  marks: {
    link: ({ value, children }) => {
      const href = value?.href || "";
      const cls = "font-medium text-pink-600 underline underline-offset-4 hover:text-pink-500";
      return href.startsWith("/") ? (
        <Link to={href} className={cls}>
          {children}
        </Link>
      ) : (
        <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>
          {children}
        </a>
      );
    },
  },
  types: {
    image: ({ value }) =>
      value?.asset ? (
        <figure className="mt-8">
          <img
            src={urlFor(value).width(1200).auto("format").url()}
            alt={value.alt || ""}
            loading="lazy"
            className="w-full rounded-3xl"
          />
          {value.caption && (
            <figcaption className="mt-2 text-center text-sm text-black/55">
              {value.caption}
            </figcaption>
          )}
        </figure>
      ) : null,
  },
};

function SideCard({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className="group flex gap-4 overflow-hidden rounded-2xl border border-black/10 bg-white p-3 transition duration-300 hover:border-pink-300 hover:shadow-[0_12px_30px_rgba(236,72,153,0.12)]"
    >
      <div className="h-24 w-24 shrink-0 overflow-hidden rounded-xl bg-pink-50">
        {post.heroImage?.asset && (
          <img
            src={urlFor(post.heroImage).width(240).auto("format").url()}
            alt={post.heroImage.alt || ""}
            loading="lazy"
            className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
          />
        )}
      </div>
      <div className="min-w-0 py-0.5">
        <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-pink-600">
          {post.kind === "story" ? "Shoot story" : "Guide"}
        </p>
        <h3 className="mt-1 text-[15px] font-semibold leading-snug text-black">{post.title}</h3>
        <p className="mt-1.5 text-xs text-black/50">
          {post.publishedAt ? dateFmt.format(new Date(post.publishedAt)) : ""}
        </p>
      </div>
    </Link>
  );
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined); // undefined = loading, null = not found
  const [related, setRelated] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setPost(undefined);
    setError(false);
    fetchPost(slug)
      .then((data) => alive && setPost(data || null))
      .catch((err) => {
        console.error("Failed to load article:", err);
        if (alive) setError(true);
      });
    fetchRelated(slug)
      .then((data) => alive && setRelated(data || []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [slug]);

  if (post === null) return <NotFound />;

  if (error) {
    return (
      <main className="min-h-screen bg-white px-6 pb-24 pt-36 text-center text-black">
        <p className="mx-auto max-w-md text-black/70">
          We could not load this article just now. Please refresh the page or try again later.
        </p>
        <Link to="/blog" className="mt-6 inline-block font-semibold text-pink-600 underline underline-offset-4">
          Back to all articles
        </Link>
      </main>
    );
  }

  if (post === undefined) {
    return (
      <main className="min-h-screen bg-white px-6 pb-24 pt-36 text-center text-black/60">
        Loading article...
      </main>
    );
  }

  const heroUrl = post.heroImage?.asset
    ? urlFor(post.heroImage).width(1400).auto("format").url()
    : null;
  const shareImage = post.heroImage?.asset
    ? urlFor(post.heroImage).width(1200).height(630).fit("crop").format("jpg").url()
    : undefined;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.metaDescription || post.excerpt,
    image: shareImage ? [shareImage] : undefined,
    datePublished: post.publishedAt,
    dateModified: post._updatedAt || post.publishedAt,
    mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
    author: { "@type": "Organization", name: "Kinews Studio Limited" },
    publisher: {
      "@type": "Organization",
      name: "Kinews Studio Limited",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/kinews-logo.jpg` },
    },
  };

  return (
    <>
      <SEO
        title={`${post.title} | Kinews Studio`}
        description={post.metaDescription || post.excerpt}
        image={shareImage}
        jsonLd={jsonLd}
      />
      <main className="min-h-screen bg-white text-black">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 pb-16 pt-28 sm:px-10 md:pt-36 lg:grid-cols-3 lg:gap-14 lg:px-16 xl:px-10">
          <article className="min-w-0 lg:col-span-2">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm font-semibold text-pink-600 hover:text-pink-500"
            >
              <ArrowLeft size={16} aria-hidden="true" />
              All articles
            </Link>

            <p className="mt-6 text-xs font-medium uppercase tracking-[0.2em] text-pink-600">
              {post.kind === "story" ? "Shoot story" : "Guide"}
              {post.category ? ` · ${post.category}` : ""}
            </p>
            <h1 className="mt-3 text-balance text-3xl font-bold leading-[1.15] tracking-tight md:text-5xl">
              {post.title}
            </h1>
            {post.publishedAt && (
              <p className="mt-4 text-sm text-black/50">
                {dateFmt.format(new Date(post.publishedAt))}
              </p>
            )}

            {heroUrl && (
              <div className="mt-8 flex h-[380px] items-center justify-center overflow-hidden rounded-3xl bg-pink-50 md:h-[520px]">
                <img
                  src={heroUrl}
                  alt={post.heroImage.alt || ""}
                  fetchPriority="high"
                  className="h-full w-auto max-w-full object-contain"
                />
              </div>
            )}

            <div className="mt-6">
              <PortableText value={post.body} components={components} />
            </div>

            {post.gallery?.length > 0 && (
              <div className="mt-12">
                <h2 className="text-2xl font-semibold tracking-tight md:text-3xl">Gallery</h2>
                <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4">
                  {post.gallery.map((img, i) => (
                    <img
                      key={i}
                      src={urlFor(img).width(900).auto("format").url()}
                      alt={img.alt || ""}
                      loading="lazy"
                      className="w-full rounded-2xl object-cover"
                    />
                  ))}
                </div>
              </div>
            )}
          </article>

          {related.length > 0 && (
            <aside className="lg:col-span-1">
              <div className="rounded-3xl bg-[#fff7fb] p-6 lg:sticky lg:top-28">
                <div className="flex items-end justify-between gap-4">
                  <h2 className="text-xl font-semibold tracking-tight">More to read</h2>
                  <Link
                    to="/blog"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-pink-600 hover:text-pink-500"
                  >
                    All articles
                    <ArrowRight size={14} aria-hidden="true" />
                  </Link>
                </div>
                <div className="mt-5 space-y-4">
                  {related.map((p) => (
                    <SideCard key={p.slug} post={p} />
                  ))}
                </div>
              </div>
            </aside>
          )}
        </div>

        <section className="bg-white">
          <div className="mx-auto max-w-7xl px-6 pb-14 sm:px-10 lg:px-16 xl:px-10">
            <CtaBand
              heading="Book Your Studio Photoshoot in Rochdale"
              text="Choose a session, pick a time and get portraits, family and maternity photos you will love."
              to="/book"
              label="Book a Studio Session"
              image={ctaStudio}
            />
          </div>
        </section>
      </main>
    </>
  );
}
